import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {LngLatLike, Map, MapMouseEvent, MapOptions} from 'maplibre-gl'
import {
  AddedOrbData,
  CollectOrbParams,
  CollectOrbResult,
  MapContext,
  MapContextType,
  PlacingOrbCallbacks,
} from './MapContext'
import LoaderObject from '@/components/MapContext/ThreeJs/loaderObject'
import OrbLoader, {OrbObject3D} from '@/components/MapContext/ThreeJs/OrbLoader'
import RedSphereLoader from '@/components/MapContext/ThreeJs/RedSphereLoader'
import OnSaleLoader from '@/components/MapContext/ThreeJs/OnSaleLoader'
import OnRentedLoader from '@/components/MapContext/ThreeJs/OnRentedLoader'
import BillboardLoader from '@/components/MapContext/ThreeJs/BillboardLoader'
import {SUN_LIGHT_UPDATE_MS, updateSunLight} from './sunLight'
import {
  DroneLoader,
  DroneConfig,
} from '@/components/MapContext/ThreeJs/DroneLoader'
import {
  createMinerObject,
  MINER_ALTITUDE,
} from '@/components/MapContext/ThreeJs/MinerObject'
import {ThreeJsLayer} from '@/components/MapContext/ThreeJs/ThreeJsLayer'
import {ObjectOfMapInterface} from '@/types/types'
import MapService from '@/services/api/Map'
import {isErrorResponse} from '@/helpers/IsErrorResponse'
import {Object3D} from 'three'
import {FontLoader} from 'three/examples/jsm/loaders/FontLoader.js'
import {Font} from 'three/examples/jsm/loaders/FontLoader.js'
import {calculateGeographicCenter, getPitch} from './mapUtils'
import {MAP_CONFIG} from './mapConfig'

interface MapProviderProps {
  children: ReactNode
  defaultOptions?: Partial<MapOptions>
}

type PlacingEventHandlers = {
  handleMouseMove: ((e: MapMouseEvent) => void) | null
  handleClick: ((e: MapMouseEvent) => void) | null
  handleMouseDown: (() => void) | null
  handleMouseUp: ((e: MapMouseEvent) => void) | null
  handleGlobalMouseUp: ((e: MouseEvent) => void) | null
  handleKeyDown: ((event: KeyboardEvent) => void) | null
}

const EMPTY_PLACING_HANDLERS: PlacingEventHandlers = {
  handleMouseMove: null,
  handleClick: null,
  handleMouseDown: null,
  handleMouseUp: null,
  handleGlobalMouseUp: null,
  handleKeyDown: null,
}

const detachPlacingHandlers = (map: Map, handlers: PlacingEventHandlers) => {
  if (handlers.handleMouseMove) map.off('mousemove', handlers.handleMouseMove)
  if (handlers.handleClick) map.off('click', handlers.handleClick)
  if (handlers.handleMouseDown) map.off('mousedown', handlers.handleMouseDown)
  if (handlers.handleMouseUp) map.off('mouseup', handlers.handleMouseUp)
  if (handlers.handleGlobalMouseUp) {
    window.removeEventListener('mouseup', handlers.handleGlobalMouseUp)
  }
  if (handlers.handleKeyDown) {
    window.removeEventListener('keydown', handlers.handleKeyDown)
  }
}

export const MapProvider: React.FC<MapProviderProps> = ({
  children,
  defaultOptions = {},
}) => {
  const mapInstanceRef = useRef<Map | null>(null)
  const [mapInstance, setMapInstance] = useState<Map | null>(null)
  const [isMapReady, setIsMapReady] = useState<boolean>(false)
  const hoveredHexIdRef = useRef<string | null>(null)
  const [hoveredOrbHexId, setHoveredOrbHexId] = useState<string | null>(null)
  const [hoveredOrbData, setHoveredOrbData] =
    useState<ObjectOfMapInterface | null>(null)
  const loaderObjectInstanceRef = useRef<LoaderObject | null>(null)
  const orbLoaderInstanceRef = useRef<OrbLoader | null>(null)
  const redSphereLoaderRef = useRef<RedSphereLoader | null>(null)
  const onSaleLoaderRef = useRef<OnSaleLoader | null>(null)
  const onRentedLoaderRef = useRef<OnRentedLoader | null>(null)
  const billboardLoaderRef = useRef<BillboardLoader | null>(null)
  const droneLoaderRef = useRef<DroneLoader | null>(null)
  const minersRef = useRef<
    globalThis.Map<string, {object: Object3D; hexId: string}>
  >(new globalThis.Map())
  const threeLayerRef = useRef<ThreeJsLayer | null>(null)
  const sunTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const fontRef = useRef<Font | null>(null)

  const [isPlacingOrb, setIsPlacingOrb] = useState<boolean>(false)
  const [placingOrbData, setPlacingOrbData] = useState<AddedOrbData | null>(
    null
  )
  const placingCallbacksRef = useRef<PlacingOrbCallbacks | null>(null)
  const previewOrbRef = useRef<Object3D | null>(null)
  const placingEventHandlersRef = useRef<PlacingEventHandlers>(
    EMPTY_PLACING_HANDLERS
  )

  const initializeLoaders = useCallback(() => {
    if (!loaderObjectInstanceRef.current) {
      loaderObjectInstanceRef.current = new LoaderObject()
    }
    if (!orbLoaderInstanceRef.current) {
      orbLoaderInstanceRef.current = new OrbLoader()
    }
    if (!redSphereLoaderRef.current) {
      redSphereLoaderRef.current = new RedSphereLoader()
    }
    if (!onSaleLoaderRef.current) {
      onSaleLoaderRef.current = new OnSaleLoader()
    }
    if (!onRentedLoaderRef.current) {
      onRentedLoaderRef.current = new OnRentedLoader()
    }
    if (!billboardLoaderRef.current) {
      billboardLoaderRef.current = new BillboardLoader()
    }
    if (!droneLoaderRef.current) {
      droneLoaderRef.current = new DroneLoader()
    }
    if (!threeLayerRef.current) {
      threeLayerRef.current = new ThreeJsLayer('threejs-3d-layer')
    }
  }, [])

  const defaultOptionsRef = useRef(defaultOptions)

  const initializeMap = useCallback(
    (
      containerId: string,
      dataOfMap: {lat: number; lng: number; zoom: number}
    ): Map | null => {
      if (mapInstanceRef.current && mapInstanceRef.current.loaded()) {
        const currentContainer = mapInstanceRef.current.getContainer()
        const containerElement = document.getElementById(containerId)

        if (
          currentContainer &&
          containerElement &&
          currentContainer === containerElement
        ) {
          if (!mapInstance) {
            setMapInstance(mapInstanceRef.current)
            setIsMapReady(true)
          }
          return mapInstanceRef.current
        }

        // Container changed (remounted or different ID) — destroy to prevent memory leak
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
        setMapInstance(null)
        setIsMapReady(false)
      }

      try {
        const initialZoom = dataOfMap.zoom || MAP_CONFIG.defaultZoom
        const initialPitch = getPitch(initialZoom)

        const mapOptions: MapOptions = {
          container: containerId,
          center: [
            dataOfMap.lng || MAP_CONFIG.defaultCenter.lng,
            dataOfMap.lat || MAP_CONFIG.defaultCenter.lat,
          ],
          zoom: initialZoom,
          style: MAP_CONFIG.style,
          maxZoom: MAP_CONFIG.maxZoom,
          pitch: initialPitch,
          minZoom: MAP_CONFIG.minZoom,
          attributionControl: false,
          ...defaultOptionsRef.current,
        }
        const map = new Map(mapOptions)
        map.on('load', async () => {
          initializeLoaders()

          map.setProjection({type: 'globe'})
          updateSunLight(map)
          if (sunTimerRef.current) clearInterval(sunTimerRef.current)
          sunTimerRef.current = setInterval(
            () => updateSunLight(map),
            SUN_LIGHT_UPDATE_MS
          )
          map.resize()
          if (
            threeLayerRef.current &&
            !map.getLayer(threeLayerRef.current.id)
          ) {
            map.addLayer(threeLayerRef.current)
          }

          loaderObjectInstanceRef.current?.initThreeLayer(
            threeLayerRef.current!
          )

          orbLoaderInstanceRef.current?.initThreeLayer(threeLayerRef.current!)
          threeLayerRef.current?.initOrbLoader(orbLoaderInstanceRef.current!)

          redSphereLoaderRef.current?.initThreeLayer(threeLayerRef.current!)

          onSaleLoaderRef.current?.initThreeLayer(threeLayerRef.current!)
          onRentedLoaderRef.current?.initThreeLayer(threeLayerRef.current!)
          if (!fontRef.current) {
            const loader = new FontLoader()
            loader.load(
              'https://threejs.org/examples/fonts/helvetiker_regular.typeface.json',
              (font) => {
                fontRef.current = font
                onSaleLoaderRef.current?.setFontRef(fontRef)
                onRentedLoaderRef.current?.setFontRef(fontRef)
              },
              undefined,
              (error) => {
                console.error('Failed to load font', error)
              }
            )
          }

          billboardLoaderRef.current?.initThreeLayer(threeLayerRef.current!)
          threeLayerRef.current?.initBillboardLoader(
            billboardLoaderRef.current!
          )

          droneLoaderRef.current?.initThreeLayer(threeLayerRef.current!)
          threeLayerRef.current?.initDroneLoader(droneLoaderRef.current!)

          await Promise.all([
            loaderObjectInstanceRef.current?.waitForLoad(),
            orbLoaderInstanceRef.current?.waitForLoad(),
            redSphereLoaderRef.current?.waitForLoad(),
            onSaleLoaderRef.current?.waitForLoad(),
            onRentedLoaderRef.current?.waitForLoad(),
            billboardLoaderRef.current?.waitForLoad(),
          ])

          setMapInstance(map)
          setIsMapReady(true)
          map.setPitch(initialPitch)

          requestAnimationFrame(() => map.resize())
        })

        mapInstanceRef.current = map
        return map
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to initialize map:', error)
        }
        return null
      }
    },
    [initializeLoaders]
  )

  const previousHoveredHexIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isMapReady || !loaderObjectInstanceRef.current) return

    const loader = loaderObjectInstanceRef.current
    let animationFrameId: number

    const tick = () => {
      const currentId = hoveredHexIdRef.current
      const prevId = previousHoveredHexIdRef.current

      if (
        prevId !== null &&
        prevId !== currentId &&
        loader.objects[Number(prevId)]
      ) {
        loader.objects[Number(prevId)].children[0].rotation.z = Math.PI / 2
      }

      if (currentId && loader.objects[Number(currentId)]) {
        loader.objects[Number(currentId)].children[0].rotation.z += 0.07
      }

      previousHoveredHexIdRef.current = currentId
      animationFrameId = requestAnimationFrame(tick)
    }

    animationFrameId = requestAnimationFrame(tick)

    return () => cancelAnimationFrame(animationFrameId)
  }, [isMapReady])

  const clearObjects = useCallback(() => {
    loaderObjectInstanceRef.current?.clearAllObjects()
  }, [])

  const show3DObjects = useCallback(() => {
    const loader = loaderObjectInstanceRef.current
    if (!loader) return
    for (const key in loader.objects) {
      loader.objects[key].visible = true
    }
  }, [])

  const hide3DObjects = useCallback(() => {
    const loader = loaderObjectInstanceRef.current
    if (!loader) return
    for (const key in loader.objects) {
      loader.objects[key].visible = false
    }
  }, [])

  const addObject = useCallback((bounds: [number, number][]) => {
    const center = calculateGeographicCenter(bounds)
    if (center && loaderObjectInstanceRef.current) {
      return loaderObjectInstanceRef.current.addObject(center)
    }
    return null
  }, [])

  // Orb methods
  const addOrb = useCallback(
    (
      bounds: [number, number][],
      orbData: ObjectOfMapInterface,
      hexId: string
    ): OrbObject3D | null => {
      const center = calculateGeographicCenter(bounds)
      if (center && orbLoaderInstanceRef.current) {
        return orbLoaderInstanceRef.current.addOrb(center, orbData, hexId)
      }
      return null
    },
    []
  )

  const removeOrb = useCallback((hexId: string) => {
    orbLoaderInstanceRef.current?.removeOrb(hexId)
  }, [])

  const clearOrbs = useCallback(() => {
    orbLoaderInstanceRef.current?.clearAllOrbs()
  }, [])

  const getOrbByHexId = useCallback((hexId: string): OrbObject3D | null => {
    return orbLoaderInstanceRef.current?.getOrbByHexId(hexId) || null
  }, [])

  const raycastOrbs = useCallback(
    (x: number, y: number): OrbObject3D | null => {
      if (!threeLayerRef.current || !orbLoaderInstanceRef.current) {
        return null
      }

      const orbs = orbLoaderInstanceRef.current.orbs
      const hitboxes: Object3D[] = []
      const hitboxToOrb = new globalThis.Map<Object3D, OrbObject3D>()

      for (const hexId in orbs) {
        const orb = orbs[hexId]
        if (orb.hitbox) {
          hitboxes.push(orb.hitbox)
          hitboxToOrb.set(orb.hitbox, orb)
        }
      }
      if (hitboxes.length === 0) {
        return null
      }

      const intersections = threeLayerRef.current.raycast(x, y, hitboxes)

      if (intersections.length > 0) {
        const intersectedHitbox = intersections[0].object
        return hitboxToOrb.get(intersectedHitbox) || null
      }

      return null
    },
    []
  )

  useEffect(() => {
    if (hoveredOrbHexId) {
      const orb = orbLoaderInstanceRef.current?.getOrbByHexId(hoveredOrbHexId)
      setHoveredOrbData(orb?.orbData || null)
    } else {
      setHoveredOrbData(null)
    }
  }, [hoveredOrbHexId])

  const collectOrb = useCallback(
    async (params: CollectOrbParams): Promise<CollectOrbResult> => {
      const {orbId, hexId, coordinates, bounds, orbData, refId} = params

      try {
        const response = await MapService.collectObjects({
          id: orbId,
          type: 3,
          hex_id: hexId,
          x: coordinates[0],
          y: coordinates[1],
          ref_id: refId,
        })

        if (response.status === 200) {
          if (isErrorResponse(response.data)) {
            return {
              success: false,
              error: response.data.message,
              localCode: response.data.local_code,
            }
          }

          const orb = addOrb(bounds, orbData, hexId)
          return {
            success: true,
            orb,
          }
        }

        return {
          success: false,
          error: 'Unexpected response status',
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        }
      }
    },
    [addOrb]
  )

  const stopPlacingOrb = useCallback(() => {
    const map = mapInstanceRef.current
    if (!map) return

    const handlers = placingEventHandlersRef.current
    detachPlacingHandlers(map, handlers)

    // const canvas = map.getCanvas()
    // canvas.style.cursor = ''

    if (previewOrbRef.current && threeLayerRef.current) {
      threeLayerRef.current.removeObject(previewOrbRef.current)
      previewOrbRef.current = null
    }

    if (placingCallbacksRef.current?.onCancel) {
      placingCallbacksRef.current.onCancel()
    }

    placingCallbacksRef.current = null
    setIsPlacingOrb(false)
    setPlacingOrbData(null)
    placingEventHandlersRef.current = EMPTY_PLACING_HANDLERS
  }, [])

  const startPlacingOrb = useCallback(
    (orbData: AddedOrbData, callbacks: PlacingOrbCallbacks) => {
      const map = mapInstanceRef.current
      if (!map || !threeLayerRef.current) {
        return
      }

      const isMiner = orbData.kind === 'miner'
      const isBillboard = !isMiner && orbData.data.type === 'BILLBOARDS'

      if (isBillboard && !billboardLoaderRef.current) {
        return
      }

      if (!isBillboard && !isMiner && !orbLoaderInstanceRef.current) {
        return
      }

      if (isPlacingOrb) {
        stopPlacingOrb()
      }

      placingCallbacksRef.current = callbacks

      const center = map.getCenter()

      if (isMiner) {
        const preview = threeLayerRef.current.addObject(
          `preview-miner-${Date.now()}`,
          createMinerObject(true),
          [center.lng, center.lat],
          MINER_ALTITUDE
        )
        previewOrbRef.current = preview
      } else if (isBillboard) {
        const preview = billboardLoaderRef.current!.createPreviewBillboard(
          [center.lng, center.lat],
          orbData.img
        )
        if (preview) {
          previewOrbRef.current = preview
        }
      } else {
        const preview = orbLoaderInstanceRef.current!.createPreviewOrb(
          [center.lng, center.lat],
          orbData.data
        )
        if (preview) {
          previewOrbRef.current = preview
        }
      }

      setPlacingOrbData(orbData)
      setIsPlacingOrb(true)

      // Устанавливаем курсор "grabbing" при начале размещения (объект уже в руке)
      const canvas = map.getCanvas()
      canvas.style.cursor = 'grabbing'

      const handleMouseMove = (e: MapMouseEvent) => {
        if (previewOrbRef.current && threeLayerRef.current) {
          threeLayerRef.current.updateObjectPosition(previewOrbRef.current, [
            e.lngLat.lng,
            e.lngLat.lat,
          ])
        }
      }

      const handleMouseDown = () => {
        // При зажатии мыши меняем курсор на "grabbing"
      }

      const handleClick = (e: MapMouseEvent) => {}

      let isPlaced = false

      const handleMouseUp = (e: MapMouseEvent) => {
        if (isPlaced) return
        isPlaced = true

        const coordinates: [number, number] = [e.lngLat.lng, e.lngLat.lat]
        const screenPoint = {x: e.point.x, y: e.point.y}

        // Возвращаем курсор обратно
        canvas.style.cursor = ''

        detachPlacingHandlers(map, {
          handleMouseMove,
          handleClick,
          handleMouseDown,
          handleMouseUp,
          handleGlobalMouseUp,
          handleKeyDown,
        })

        callbacks.onPlace(coordinates, screenPoint)
      }

      const handleGlobalMouseUp = (e: MouseEvent) => {
        if (isPlaced) return

        const rect = canvas.getBoundingClientRect()
        const isOnMap =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom

        if (!isOnMap) {
          isPlaced = true
          // Возвращаем курсор обратно
          canvas.style.cursor = ''
          detachPlacingHandlers(map, {
            handleMouseMove,
            handleClick,
            handleMouseDown,
            handleMouseUp,
            handleGlobalMouseUp,
            handleKeyDown,
          })
          if (callbacks.onCancel) {
            callbacks.onCancel()
          }
        }
      }

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          stopPlacingOrb()
        }
      }

      placingEventHandlersRef.current = {
        handleMouseMove,
        handleClick,
        handleMouseDown,
        handleMouseUp,
        handleGlobalMouseUp,
        handleKeyDown,
      }

      map.on('mousemove', handleMouseMove)
      map.on('mousedown', handleMouseDown)
      map.on('mouseup', handleMouseUp)
      window.addEventListener('mouseup', handleGlobalMouseUp)
      window.addEventListener('keydown', handleKeyDown)
    },
    [isPlacingOrb, stopPlacingOrb]
  )

  const previousHoveredOrbHexIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isMapReady || !orbLoaderInstanceRef.current) return

    const loader = orbLoaderInstanceRef.current
    const previousOrbId = previousHoveredOrbHexIdRef.current
    const currentOrbId = hoveredOrbHexId

    const orbIdToResetInCleanup = previousOrbId

    if (previousOrbId !== null && previousOrbId !== currentOrbId) {
      const prevOrb = loader.orbs[previousOrbId]
      if (prevOrb && prevOrb.children[0]) {
      }
    }

    previousHoveredOrbHexIdRef.current = currentOrbId

    if (!currentOrbId) {
      return
    }

    let animationFrameId: number

    const animate = () => {
      const orb = loader.orbs[currentOrbId]
      if (orb && orb.children[0]) {
        orb.children[0].rotation.y += 0.02
      }
      animationFrameId = requestAnimationFrame(animate)
    }

    animationFrameId = requestAnimationFrame(animate)

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
      if (orbIdToResetInCleanup) {
        const orb = loader.orbs[orbIdToResetInCleanup]
        if (orb && orb.children[0]) {
        }
      }
    }
  }, [isMapReady, hoveredOrbHexId])

  const destroyMap = useCallback((): void => {
    if (sunTimerRef.current) {
      clearInterval(sunTimerRef.current)
      sunTimerRef.current = null
    }
    if (mapInstanceRef.current) {
      const container = mapInstanceRef.current.getContainer()
      mapInstanceRef.current.remove()
      mapInstanceRef.current = null
      setMapInstance(null)
      setIsMapReady(false)
      if (container && container.parentNode) {
        while (container.firstChild) {
          container.removeChild(container.firstChild)
        }
      }
    }
  }, [])

  const updatePitch = useCallback(() => {
    if (!mapInstanceRef.current) return
    const currentZoom = mapInstanceRef.current.getZoom()
    const newPitch = getPitch(currentZoom)
    mapInstanceRef.current.easeTo({
      pitch: newPitch,
      duration: 300,
    })
  }, [])

  const flyTo = useCallback(
    (coordinates: LngLatLike, zoom: number = 12): void => {
      mapInstanceRef.current?.flyTo({
        center: coordinates,
        zoom,
        essential: true,
        speed: 2,
      })
    },
    []
  )

  const getMap = useCallback((): Map | null => {
    return mapInstanceRef.current
  }, [])

  const addRedSphere = useCallback((bounds: [number, number][]) => {
    const center = calculateGeographicCenter(bounds)
    if (center && redSphereLoaderRef.current) {
      return redSphereLoaderRef.current.addSphere(center)
    }
    return null
  }, [])

  const clearRedSpheres = useCallback(() => {
    redSphereLoaderRef.current?.clearAllSpheres()
  }, [])

  const addOnSaleObject = useCallback((bounds: [number, number][]) => {
    const center = calculateGeographicCenter(bounds)
    if (center && onSaleLoaderRef.current) {
      return onSaleLoaderRef.current.addOnSaleObject(center)
    }
    return null
  }, [])

  const clearOnSaleObjects = useCallback(() => {
    onSaleLoaderRef.current?.clearAllOnSaleObjects()
  }, [])

  const removeOnSaleObject = useCallback((object: Object3D) => {
    onSaleLoaderRef.current?.removeOnSaleObject(object)
  }, [])

  const addOnRentedObject = useCallback((bounds: [number, number][]) => {
    const center = calculateGeographicCenter(bounds)
    if (center && onRentedLoaderRef.current) {
      return onRentedLoaderRef.current.addOnRentedObject(center)
    }
    return null
  }, [])

  const clearOnRentedObjects = useCallback(() => {
    onRentedLoaderRef.current?.clearAllOnRentedObjects()
  }, [])

  const removeOnRentedObject = useCallback((object: Object3D) => {
    onRentedLoaderRef.current?.removeOnRentedObject(object)
  }, [])

  const addHalfSphere = useCallback(
    (center: [number, number], color?: number): Object3D | null => {
      if (!threeLayerRef.current) return null

      const id = `dome-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

      const useShader = color === undefined

      return threeLayerRef.current.addHalfSphere(
        id,
        center,
        undefined,
        color,
        0,
        useShader
      )
    },
    []
  )

  const removeHalfSphere = useCallback((dome: Object3D) => {
    if (!threeLayerRef.current) return
    threeLayerRef.current.removeObject(dome)
  }, [])

  const addBillboard = useCallback(
    (bounds: [number, number][], hexId: string, imageUrl?: string) => {
      const center = calculateGeographicCenter(bounds)
      if (center && billboardLoaderRef.current) {
        return billboardLoaderRef.current.addBillboard(center, hexId, imageUrl)
      }
      return null
    },
    []
  )

  const clearBillboards = useCallback(() => {
    billboardLoaderRef.current?.clearAllBillboards()
  }, [])

  const addDrone = useCallback((id: string, config: DroneConfig) => {
    droneLoaderRef.current?.addDrone(id, config)
  }, [])

  const removeDrone = useCallback((id: string) => {
    droneLoaderRef.current?.removeDrone(id)
  }, [])

  const clearDrones = useCallback(() => {
    droneLoaderRef.current?.clearAllDrones()
  }, [])

  const getDrones = useCallback(() => {
    return droneLoaderRef.current?.getDrones() ?? []
  }, [])

  const addMiner = useCallback(
    (id: string, lngLat: [number, number], hexId: string): Object3D | null => {
      if (!threeLayerRef.current) return null
      const key = `miner-${id}`
      if (minersRef.current.has(key)) return null

      const object = threeLayerRef.current.addObject(
        key,
        createMinerObject(),
        lngLat,
        MINER_ALTITUDE
      )
      minersRef.current.set(key, {object, hexId})
      return object
    },
    []
  )

  const clearMiners = useCallback(() => {
    minersRef.current.forEach((_, key) => {
      threeLayerRef.current?.removeObject(key)
    })
    minersRef.current.clear()
  }, [])

  const raycastMiners = useCallback((x: number, y: number) => {
    if (!threeLayerRef.current || minersRef.current.size === 0) return null

    const objects: Object3D[] = []
    const objectToHexId = new globalThis.Map<Object3D, string>()
    minersRef.current.forEach(({object, hexId}) => {
      objects.push(object)
      objectToHexId.set(object, hexId)
    })

    const intersections = threeLayerRef.current.raycast(x, y, objects)
    if (intersections.length === 0) return null

    let node: Object3D | null = intersections[0].object
    while (node && !objectToHexId.has(node)) {
      node = node.parent
    }

    return node ? (objectToHexId.get(node) ?? null) : null
  }, [])

  const raycastDrones = useCallback((x: number, y: number): string | null => {
    if (!threeLayerRef.current || !droneLoaderRef.current) return null

    const drones = droneLoaderRef.current.getHitboxes()
    if (drones.length === 0) return null

    const hitboxes: Object3D[] = []
    const hitboxToId = new globalThis.Map<Object3D, string>()
    drones.forEach(({id, hitbox}) => {
      hitboxes.push(hitbox)
      hitboxToId.set(hitbox, id)
    })

    const intersections = threeLayerRef.current.raycast(x, y, hitboxes)
    if (intersections.length === 0) return null

    return hitboxToId.get(intersections[0].object) || null
  }, [])

  const setHoveredHexId = useCallback((id: string | null) => {
    hoveredHexIdRef.current = id
  }, [])

  const contextValue: MapContextType = useMemo(
    () => ({
      mapInstance,
      isMapReady,
      initializeMap,
      destroyMap,
      flyTo,
      getMap,
      updatePitch,
      addObject,
      clearObjects,
      hoveredHexId: hoveredHexIdRef.current,
      setHoveredHexId,
      show3DObjects,
      hide3DObjects,
      addOrb,
      removeOrb,
      clearOrbs,
      getOrbByHexId,
      hoveredOrbHexId,
      setHoveredOrbHexId,
      hoveredOrbData,
      startPlacingOrb,
      stopPlacingOrb,
      isPlacingOrb,
      placingOrbData,
      collectOrb,
      addRedSphere,
      clearRedSpheres,
      addOnSaleObject,
      clearOnSaleObjects,
      removeOnSaleObject,
      addOnRentedObject,
      clearOnRentedObjects,
      removeOnRentedObject,
      addHalfSphere,
      removeHalfSphere,
      raycastOrbs,
      addBillboard,
      clearBillboards,
      addDrone,
      removeDrone,
      clearDrones,
      raycastDrones,
      getDrones,
      addMiner,
      clearMiners,
      raycastMiners,
    }),
    [
      mapInstance,
      isMapReady,
      initializeMap,
      destroyMap,
      flyTo,
      getMap,
      updatePitch,
      addObject,
      clearObjects,
      setHoveredHexId,
      show3DObjects,
      hide3DObjects,
      addOrb,
      removeOrb,
      clearOrbs,
      getOrbByHexId,
      hoveredOrbHexId,
      hoveredOrbData,
      startPlacingOrb,
      stopPlacingOrb,
      isPlacingOrb,
      placingOrbData,
      collectOrb,
      addRedSphere,
      clearRedSpheres,
      addOnSaleObject,
      clearOnSaleObjects,
      removeOnSaleObject,
      addOnRentedObject,
      clearOnRentedObjects,
      removeOnRentedObject,
      addHalfSphere,
      removeHalfSphere,
      raycastOrbs,
      addBillboard,
      clearBillboards,
      addDrone,
      removeDrone,
      clearDrones,
      raycastDrones,
      getDrones,
      addMiner,
      clearMiners,
      raycastMiners,
    ]
  )

  return (
    <MapContext.Provider value={contextValue}>{children}</MapContext.Provider>
  )
}
